import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";
import { logCrud } from "@/services/activities";

const clientSchema = z.object({
  nome: z.string().min(1, "Nome é obrigatório"),
  cpf_cnpj: z.string().optional(),
  telefone: z.string().optional(),
  whatsapp: z.string().optional(),
  email: z.string().email("E-mail inválido").optional().or(z.literal("")),
});

type ClientFormData = z.infer<typeof clientSchema>;

interface QuickAddClientModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClientCreated?: (clientId: string) => void;
}

export function QuickAddClientModal({ open, onOpenChange, onClientCreated }: QuickAddClientModalProps) {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();

  const form = useForm<ClientFormData>({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      nome: "",
      cpf_cnpj: "",
      telefone: "",
      whatsapp: "",
      email: "",
    },
  });

  const createClientMutation = useMutation({
    mutationFn: async (data: ClientFormData) => {
      if (!tenantId) throw new Error("Tenant ID não encontrado");
      
      const { data: client, error } = await supabase
        .from("clients")
        .insert([{
          nome: data.nome,
          cpf_cnpj: data.cpf_cnpj || null,
          telefone: data.telefone || null,
          whatsapp: data.whatsapp || null,
          email: data.email || null,
          tenant_id: tenantId,
        }])
        .select()
        .single();

      if (error) throw error;
      return client;
    },
    onSuccess: async (client) => {
      toast.success("Cliente cadastrado com sucesso!");
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      if (client?.id) {
        void logCrud({ entitySlug: "entity:clients", operationSlug: "create", entityId: client.id, entityName: client.nome, screenSlug: "clientes" });
      }
      onClientCreated?.(client.id);
      onOpenChange(false);
      form.reset();
    },
    onError: (error: Error) => {
      toast.error("Erro ao cadastrar cliente: " + error.message);
    },
  });

  const onSubmit = (data: ClientFormData) => {
    createClientMutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <div className="flex max-h-[80vh] flex-col">
          <DialogHeader className="sticky top-0 z-10 bg-background px-6 py-4 border-b relative">
            <DialogTitle>Cadastro Rápido de Cliente</DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-4 h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <Form {...form}>
              <form id="quick-client-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="nome"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome *</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="cpf_cnpj"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CPF/CNPJ</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="telefone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telefone</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="whatsapp"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>WhatsApp</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>E-mail</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

              </form>
            </Form>
          </div>

          <div className="bg-background px-6 py-4 border-t flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" form="quick-client-form" disabled={createClientMutation.isPending}>
              {createClientMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Cadastrar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
